import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UsersFour, ArrowsClockwise, ShieldCheck, GraduationCap, UserPlus, Copy } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

const Row = ({ icon: Icon, title, sub, right, testId }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-[#E5E7EB]/60 last:border-0" data-testid={testId}>
    <span className="h-8 w-8 rounded-lg bg-[#0052FF]/10 flex items-center justify-center shrink-0"><Icon size={16} weight="duotone" className="text-[#0052FF]" /></span>
    <div className="min-w-0 mr-auto">
      <div className="text-xs font-bold text-[#0A0D14]">{title}</div>
      <div className="text-[11px] text-[#8a90a0] truncate">{sub}</div>
    </div>
    {right}
  </div>
);

export const AudienceEngine = () => {
  const [s, setS] = useState(null);
  const [account, setAccount] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => api.get("/audience-engine/status").then(({ data }) => {
    setS(data);
    if (!account && data.accounts?.length) setAccount(data.accounts[0].id);
  }).catch(() => setS({ configured: false, accounts: [] }));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const { data } = await fn();
      toast.success(okMsg(data), { duration: 9000 });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { duration: 12000 });
    } finally { setBusy(""); }
  };

  const setup = () => run("setup", () => api.post("/audience-engine/setup", { account_id: account }),
    (d) => `Audiences built — ${d.leads_uploaded || 0} lead submitters + ${d.enrolled_uploaded || 0} enrolled students uploaded${d.lookalike_created ? " · 1% India Lookalike created" : " · Lookalike will be created once Meta finishes matching the seed"}`);
  const refresh = () => run("refresh", () => api.post("/audience-engine/refresh"),
    (d) => `Refreshed — ${d.leads_uploaded || 0} submitters, ${d.enrolled_uploaded || 0} enrolled${d.lookalike_created ? " · Lookalike created" : ""}`);
  const exclude = () => run("exclude", () => api.post("/audience-engine/apply-exclusions"),
    () => "Applying enrolled-student exclusion to all active ad sets in the background — check back in ~2 minutes");

  const toggle = async (patch) => {
    try {
      await api.put("/audience-engine/settings", { auto_refresh: s.auto_refresh, auto_exclude: s.auto_exclude, ...patch });
      setS({ ...s, ...patch });
      toast.success("Saved");
    } catch { toast.error("Could not save"); }
  };

  const fmtT = (t) => { const d = new Date(t); return isNaN(d) ? "never" : d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); };

  if (!s) return null;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-5" data-testid="audience-engine-card">
      <div className="flex flex-wrap items-center gap-3">
        <span className="h-10 w-10 rounded-lg bg-ai/10 flex items-center justify-center"><UsersFour size={20} weight="duotone" className="text-ai" /></span>
        <div className="mr-auto">
          <h2 className="font-heading text-base font-extrabold tracking-tight text-[#0A0D14]">Audience Engine</h2>
          <p className="text-[11px] text-[#8a90a0]">
            {s.configured ? `Auto-built from your leads · last refresh ${fmtT(s.last_refresh)}` : "Auto-build Custom Audiences from your leads + a 1% India Lookalike, and exclude enrolled students"}
          </p>
        </div>
        {!s.configured ? (
          <div className="flex items-center gap-2">
            {s.accounts?.length > 1 && (
              <Select value={account} onValueChange={setAccount}>
                <SelectTrigger className="w-56 h-9 text-xs font-semibold" data-testid="ae-account-select"><SelectValue /></SelectTrigger>
                <SelectContent>{s.accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button onClick={setup} disabled={busy === "setup" || !account} data-testid="ae-setup-btn"
              className="rounded-full bg-ai hover:bg-[#5457e0] text-white font-bold h-9">
              {busy === "setup" ? "Building on Meta…" : "Build my audiences"}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button onClick={refresh} disabled={busy === "refresh"} size="sm" data-testid="ae-refresh-btn"
              className="rounded-full bg-[#0A0D14] hover:bg-black text-white font-bold text-xs h-9">
              <ArrowsClockwise size={13} weight="bold" className={`mr-1 ${busy === "refresh" ? "animate-spin" : ""}`} /> Refresh now
            </Button>
            <Button onClick={exclude} disabled={busy === "exclude"} size="sm" variant="outline" data-testid="ae-exclude-btn"
              className="rounded-full border-[#E5E7EB] font-bold text-xs h-9">
              <ShieldCheck size={13} weight="bold" className="mr-1" /> {busy === "exclude" ? "Applying…" : "Apply exclusions"}
            </Button>
          </div>
        )}
      </div>
      {s.configured && (
        <>
          <div className="mt-3">
            <Row icon={UserPlus} title="All Lead Submitters" testId="ae-row-leads"
              sub={`Custom Audience · ${(s.leads_uploaded || 0).toLocaleString("en-IN")} hashed contacts uploaded · id ${s.leads_audience_id}`}
              right={<span className="rounded-full bg-success/10 text-[#00953e] text-[10px] font-bold px-2.5 py-1">LIVE</span>} />
            <Row icon={GraduationCap} title="Enrolled Students (exclusion list)" testId="ae-row-enrolled"
              sub={`${(s.enrolled_uploaded || 0).toLocaleString("en-IN")} admissions uploaded${s.exclusions_running ? " · applying exclusions to ad sets…" : s.last_exclusions_at ? ` · last applied to ${s.last_exclusions_applied || 0} ad set(s)` : ""} — excluded so you never pay to re-target enrolled students`}
              right={<span className="rounded-full bg-success/10 text-[#00953e] text-[10px] font-bold px-2.5 py-1">LIVE</span>} />
            <Row icon={Copy} title="Lookalike 1% · India" testId="ae-row-lookalike"
              sub={s.lookalike_id ? `Built from Lead Submitters · id ${s.lookalike_id} — use it in a cold ad set` : (s.lookalike_error ? `Waiting: ${s.lookalike_error}` : "Will be created once Meta finishes matching the seed audience (retries every refresh)")}
              right={s.lookalike_id
                ? <span className="rounded-full bg-success/10 text-[#00953e] text-[10px] font-bold px-2.5 py-1">LIVE</span>
                : <span className="rounded-full bg-warning/10 text-[#b07600] text-[10px] font-bold px-2.5 py-1">PENDING</span>} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-[11px] font-bold text-[#525866]">
              <Switch checked={!!s.auto_refresh} onCheckedChange={(v) => toggle({ auto_refresh: v })} data-testid="ae-auto-refresh-switch" />
              Auto-refresh every 3h sync
            </label>
            <label className="flex items-center gap-2 text-[11px] font-bold text-[#525866]">
              <Switch checked={!!s.auto_exclude} onCheckedChange={(v) => toggle({ auto_exclude: v })} data-testid="ae-auto-exclude-switch" />
              Auto-exclude enrolled students from all active ad sets
            </label>
          </div>
        </>
      )}
    </div>
  );
};
